// The Spades card-table set: table, four suit chairs (Spade N, Diamond E,
// Club S, Heart W, each facing the centre) and a rug, authored in Blender
// with fixed colours (tools/blender/card-table-set.blend →
// public/models/games/card-table-set.glb). Metres, +Y up, floor at 0, chair
// positions identical to tableLayout's seatXZ/seatYaw, so the seat markers
// and card placement line up without any transforms here.
//
// useGLTF caches one scene for every table, so each instance renders a clone.

import React, { Component, Suspense, useMemo } from 'react';
import * as THREE from 'three';
import { useGLTF } from '@react-three/drei';
import { TABLE_R, TABLE_TOP, SEAT_Y, seatXZ, seatYaw } from './tableLayout';

export const CARD_TABLE_URL = '/models/games/card-table-set.glb';

// Fallback palette (matches the set: burgundy felt, charcoal lacquer, gold).
const FELT = '#5a1424', FRAME = '#1d2226', GOLD = '#b8862e';

function CardTableGLB() {
  const { scene } = useGLTF(CARD_TABLE_URL);
  const set = useMemo(() => {
    const root = scene.clone(true);
    root.traverse((c) => {
      // Furniture is decoration: never ground to walk on (keeps seating exact).
      c.userData.collidable = false;
      const m = c as THREE.Mesh;
      if (m.isMesh) { m.castShadow = m.name !== 'Rug'; m.receiveShadow = true; }
    });
    return root;
  }, [scene]);
  return <primitive object={set} />;
}

/** Simple primitive set: shown while the GLB loads, or if it fails. */
export function PrimitiveTableSet() {
  return (
    <>
      <mesh position={[0, TABLE_TOP - 0.02, 0]} castShadow receiveShadow userData={{ collidable: false }}>
        <cylinderGeometry args={[TABLE_R, TABLE_R, 0.04, 48]} />
        <meshStandardMaterial color={FELT} roughness={0.95} />
      </mesh>
      <mesh position={[0, TABLE_TOP - 0.03, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[TABLE_R, 0.028, 10, 48]} />
        <meshStandardMaterial color={FRAME} roughness={0.6} />
      </mesh>
      <mesh position={[0, (TABLE_TOP - 0.05) / 2, 0]} castShadow>
        <cylinderGeometry args={[0.07, 0.1, TABLE_TOP - 0.05, 16]} />
        <meshStandardMaterial color={FRAME} roughness={0.6} />
      </mesh>
      <mesh position={[0, 0.02, 0]} receiveShadow>
        <cylinderGeometry args={[0.36, 0.4, 0.04, 32]} />
        <meshStandardMaterial color={GOLD} roughness={0.5} metalness={0.6} />
      </mesh>
      {[0, 1, 2, 3].map((i) => {
        const [x, z] = seatXZ(i);
        return (
          <group key={i} position={[x, 0, z]} rotation={[0, seatYaw(i), 0]}>
            <mesh position={[0, SEAT_Y - 0.03, 0]} castShadow>
              <boxGeometry args={[0.46, 0.06, 0.46]} />
              <meshStandardMaterial color={FELT} roughness={0.7} />
            </mesh>
            <mesh position={[0, SEAT_Y + 0.27, -0.21]} castShadow>
              <boxGeometry args={[0.46, 0.5, 0.05]} />
              <meshStandardMaterial color={FRAME} roughness={0.7} />
            </mesh>
            {[[-0.19, -0.19], [0.19, -0.19], [-0.19, 0.19], [0.19, 0.19]].map(([lx, lz], k) => (
              <mesh key={k} position={[lx, (SEAT_Y - 0.06) / 2, lz]}>
                <cylinderGeometry args={[0.018, 0.018, SEAT_Y - 0.06, 8]} />
                <meshStandardMaterial color={FRAME} />
              </mesh>
            ))}
          </group>
        );
      })}
    </>
  );
}

class FurnitureBoundary extends Component<{ fallback: React.ReactNode; children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err: unknown) { console.warn('[game-table] model failed, using simple furniture', err); }
  render() { return this.state.failed ? this.props.fallback : this.props.children; }
}

/** Table + 4 chairs + rug standing on the floor (local origin = floor centre). */
export function CardTableSet() {
  const fallback = <PrimitiveTableSet />;
  return (
    <FurnitureBoundary fallback={fallback}>
      <Suspense fallback={fallback}>
        <CardTableGLB />
      </Suspense>
    </FurnitureBoundary>
  );
}
